import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
} from '@mui/icons-material';
import api from '../axiosConfig';

const Packages = () => {
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [include, setInclude] = useState('active');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/packages?include=${include}`);
      setPackages(res.data.packages || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load packages');
    } finally {
      setLoading(false);
    }
  }, [include]);

  useEffect(() => {
    load();
  }, [load]);

  const handleIncludeChange = (_event, newInclude) => {
    if (newInclude !== null) {
      setInclude(newInclude);
    }
  };

  const handleArchive = async (pkg) => {
    setActionError(null);
    if (!window.confirm(`Archive "${pkg.name}"? Clients will no longer see it, but booking history is kept.`)) {
      return;
    }
    try {
      await api.post(`/packages/${pkg.id}/archive`);
      await load();
    } catch (err) {
      setActionError(err.response?.data?.error || 'Archive failed');
    }
  };

  const handleRestore = async (pkg) => {
    setActionError(null);
    try {
      await api.post(`/packages/${pkg.id}/restore`);
      await load();
    } catch (err) {
      setActionError(err.response?.data?.error || 'Restore failed');
    }
  };

  const handleDelete = async (pkg) => {
    setActionError(null);
    if (pkg.booking_count > 0) {
      setActionError('Package has booking history; archive it instead.');
      return;
    }
    if (!window.confirm(`Permanently delete "${pkg.name}"? This cannot be undone.`)) {
      return;
    }
    try {
      await api.delete(`/packages/${pkg.id}`);
      await load();
    } catch (err) {
      setActionError(err.response?.data?.error || 'Delete failed');
    }
  };

  const formatMoney = (value) => {
    if (value === null || value === undefined) return '—';
    return `£${Number(value).toFixed(2)}`;
  };

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#f5f5f5' }}>
      {/* Compact Header */}
      <Box
        sx={{
          bgcolor: 'white',
          borderBottom: '1px solid #e0e0e0',
          px: 3,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="h6" fontWeight="600">
          Packages
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => navigate('/solo/packages/new')}
        >
          New Package
        </Button>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, p: 2, overflow: 'auto' }}>
        <Paper sx={{ p: 3 }}>
          {/* Filter */}
          <Box sx={{ mb: 2 }}>
            <ToggleButtonGroup
              value={include}
              exclusive
              onChange={handleIncludeChange}
              size="small"
              color="primary"
            >
              <ToggleButton value="active">Active</ToggleButton>
              <ToggleButton value="archived">Archived</ToggleButton>
              <ToggleButton value="all">All</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {actionError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
              {actionError}
            </Alert>
          )}

          {loading ? (
            <Typography>Loading packages...</Typography>
          ) : packages.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {include === 'archived' ? 'No archived packages' : 'No packages yet'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Bundle services together to offer combined deals to your clients
              </Typography>
              {include !== 'archived' && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/solo/packages/new')}
                >
                  Create First Package
                </Button>
              )}
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Name</strong></TableCell>
                    <TableCell><strong>Slots</strong></TableCell>
                    <TableCell><strong>Price Override</strong></TableCell>
                    <TableCell><strong>Deposit Override</strong></TableCell>
                    <TableCell><strong>Bookings</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell align="right"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {packages.map((pkg) => (
                    <TableRow key={pkg.id} hover>
                      <TableCell>
                        <Typography
                          variant="body1"
                          fontWeight="medium"
                          sx={{
                            color: 'primary.main',
                            cursor: 'pointer',
                            '&:hover': { textDecoration: 'underline' },
                          }}
                          onClick={() => navigate(`/solo/packages/${pkg.id}`)}
                        >
                          {pkg.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={pkg.slot_count}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{formatMoney(pkg.price_override)}</TableCell>
                      <TableCell>{formatMoney(pkg.deposit_override)}</TableCell>
                      <TableCell>{pkg.booking_count}</TableCell>
                      <TableCell>
                        {pkg.active ? (
                          <Chip label="Active" size="small" color="success" />
                        ) : (
                          <Chip label="Archived" size="small" color="warning" />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => navigate(`/solo/packages/${pkg.id}`)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        {pkg.active ? (
                          <Tooltip title="Archive">
                            <IconButton size="small" onClick={() => handleArchive(pkg)}>
                              <ArchiveIcon />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Restore">
                            <IconButton size="small" onClick={() => handleRestore(pkg)}>
                              <UnarchiveIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip
                          title={
                            pkg.booking_count > 0
                              ? 'Has booking history — archive instead'
                              : 'Permanent delete'
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDelete(pkg)}
                              disabled={pkg.booking_count > 0}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default Packages;